/**
 * Dev Mode Injector Component
 * 
 * Loads scam-sms-candidates.json test cases for manual testing
 * Enabled in preview and production via DEV_MODE_ENABLED config
 */
import { useState, useEffect } from 'react';
import { DEV_MODE_ENABLED } from '../../config';

interface DevTestcase {
  id: string;
  content: string;
  typeOfScam: 'scam' | 'safe';
  description: string;
  expectedResult: string;
}

interface DevModeInjectorProps {
  onInjectTestCase: (content: string) => void;
}

export function DevModeInjector({ onInjectTestCase }: DevModeInjectorProps) {
  const [testCases, setTestCases] = useState<DevTestcase[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!DEV_MODE_ENABLED) {
      return;
    }

    setLoading(true);
    // Load test cases from dev folder
    fetch('/dev/scam-sms-candidates.json')
      .then(res => res.json())
      .then((data: DevTestcase[]) => {
        setTestCases(data);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to load dev test cases:', err);
        setError('Failed to load test cases');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (!DEV_MODE_ENABLED) {
    return null;
  }

  const handleTestCaseClick = (testCase: DevTestcase) => {
    onInjectTestCase(testCase.content);
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-purple-700 flex items-center gap-2"
          title="Dev Mode: Load Test Cases"
        >
          <span>🧪</span>
          <span className="text-sm font-medium">Dev Test Cases</span>
        </button>
      ) : (
        <div className="bg-white rounded-lg shadow-2xl border-2 border-purple-400 w-96 max-h-96 overflow-hidden flex flex-col">
          <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>🧪</span>
              <h3 className="font-bold text-sm">Dev Test Cases</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="overflow-y-auto flex-1 p-4">
            {loading && (
              <p className="text-sm text-gray-500">Loading test cases...</p>
            )}
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
            {!loading && !error && testCases.length === 0 && (
              <p className="text-sm text-gray-500">No test cases found</p>
            )}
            {testCases.length > 0 && (
              <div className="space-y-2">
                {testCases.map((testCase) => (
                  <button
                    key={testCase.id}
                    onClick={() => handleTestCaseClick(testCase)}
                    className="w-full text-left p-3 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono text-gray-500">
                        {testCase.id}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          testCase.typeOfScam === 'scam'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {testCase.typeOfScam}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-1 line-clamp-2">
                      {testCase.content}
                    </p>
                    <p className="text-xs text-gray-500">
                      Expected: {testCase.expectedResult}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
